import React from 'react';
import { useSelector } from 'react-redux';
import Tooltip from '../Tooltip';
import './QueryContextBar.scss';
import { getCountryCode } from '@/components/dropdown/MarketsDropdown/utils';
import IconPlaceholder from '@/components/common/IconPlaceholder/IconPlaceholder';

const BrandAvatar = ({ brand, brandEntities }) => {
    const appBrand = brandEntities?.[brand.id];
    const logo = appBrand?.logo;
    const label = appBrand?.displayName || appBrand?.name || brand.name || '?';
    const initial = label.charAt(0).toUpperCase();

    return (
        <Tooltip content={label} placement="top" variant="dark" arrow={true}>
            <div className="query-stack-icon brand-icon" aria-label={label}>
                {logo ? (
                    <>
                        <img
                            src={logo}
                            alt=""
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = e.target.nextElementSibling;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                        <span aria-hidden style={{ display: 'none' }}>{initial}</span>
                    </>
                ) : (
                    <span aria-hidden>{initial}</span>
                )}
            </div>
        </Tooltip>
    );
};

const MarketAvatar = ({ market }) => {
    const name = market?.name || 'Unknown';
    const identity = market?.identity || '';

    const identityCode = identity?.startsWith('US') ? 'US' : identity;
    const countryCode = getCountryCode(name, identityCode, identity);

    if (countryCode === 'spiritDrinkers') {
        return (
            <img
                data-testid="circle-country-flag"
                className="h-[28px] w-[28px] min-h-[28px] min-w-[28px]"
                title={name}
                src="/images/market/spirit_drinkers.png"
                alt={name}
            />
        );
    }

    if (countryCode) {
        return (
            <img
                data-testid="circle-country-flag"
                className="h-[28px] w-[28px] min-h-[28px] min-w-[28px]"
                title={name}
                src={`https://hatscripts.github.io/circle-flags/flags/${countryCode}.svg`}
                alt={name}
            />
        );
    }

    if (name && name.trim()) {
        return (
            <IconPlaceholder title={name} size="small" withBorder />
        );
    }

    return (
        <div className="h-[28px] w-[28px] min-h-[28px] min-w-[28px] flex items-center justify-center rounded-full bg-[#292D33] text-white text-xs font-medium">
            ?
        </div>
    );
};

const QueryContextBar = ({ data }) => {
    const brandEntities = useSelector((state) => state.activeGroup?.brands?.entities);
    // Segments are keyed by ULID code in the store; resolve the display name from
    // the catalog (the backend sends the code as `id`, and only knows the name for
    // segments created this session — screen segments arrive name-less).
    const segmentEntities = useSelector((state) => state.segments?.segments?.entities);

    if (!data) return null;

    const { brands = [], markets = [], segments = [], filters = [], period } = data;

    const formatDateRange = (start, end) => {
        if (!start || !end) return null;
        try {
            const sParts = start.split('-');
            const eParts = end.split('-');
            if (sParts.length !== 3 || eParts.length !== 3) return null;
            const s = new Date(sParts[0], sParts[1] - 1, sParts[2]);
            const e = new Date(eParts[0], eParts[1] - 1, eParts[2]);
            const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            return `${fmt(s)} – ${fmt(e)}`;
        } catch (err) {
            return null;
        }
    };

    const buildFilterDescriptor = (filter) => {
        if (!filter) return 'Filter';

        if (Array.isArray(filter.filterOptionNames) && filter.filterOptionNames.length > 0) {
            return filter.filterOptionNames.join(', ');
        }

        if (filter.ageRange && typeof filter.ageRange.min === 'number' && typeof filter.ageRange.max === 'number') {
            return `Age ${filter.ageRange.min}\u2013${filter.ageRange.max}`;
        }

        return 'Filter';
    };

    const buildFilterLines = (filters) => {
        if (!Array.isArray(filters) || filters.length === 0) return [];
        return filters
            .map((filter) => buildFilterDescriptor(filter))
            .filter(Boolean);
    };

    const buildFilterTooltip = (filters) => {
        const lines = buildFilterLines(filters);
        if (!lines.length) return '';

        return (
            <ul className="query-filter-tooltip-list">
                {lines.map((line, index) => (
                    // Using index as key is acceptable here because the list is small and static per render
                    <li key={index}>{line}</li>
                ))}
            </ul>
        );
    };

    const buildSingleFilterLabel = (filter) => {
        if (!filter) return 'Filter';

        if (Array.isArray(filter.filterOptionNames) && filter.filterOptionNames.length === 1) {
            return filter.filterOptionNames[0];
        }

        if (Array.isArray(filter.filterOptionNames) && filter.filterOptionNames.length > 1) {
            return `${filter.filterOptionNames.length} options`;
        }

        if (filter.ageRange && typeof filter.ageRange.min === 'number' && typeof filter.ageRange.max === 'number') {
            return `Age ${filter.ageRange.min}\u2013${filter.ageRange.max}`;
        }

        return 'Filter';
    };

    // Prefer the catalog label (keyed by the segment's ULID code, which the
    // backend sends as `id`); fall back to the payload name, then a placeholder.
    const resolveSegmentName = (segment) =>
        segmentEntities?.[segment?.id]?.label || segment?.name || 'Segment';

    const buildSegmentLines = (segments) => {
        if (!Array.isArray(segments) || segments.length === 0) return [];
        return segments.map(resolveSegmentName).filter(Boolean);
    };

    const buildSegmentTooltip = (segments) => {
        const lines = buildSegmentLines(segments);
        if (!lines.length) return '';

        return (
            <ul className="query-segment-tooltip-list">
                {lines.map((line, index) => (
                    <li key={index}>{line}</li>
                ))}
            </ul>
        );
    };

    const buildNameListTooltip = (names, listClassName) => {
        const safeNames = (names || []).filter(Boolean);
        if (!safeNames.length) return '';

        return (
            <ul className={listClassName}>
                {safeNames.map((name, index) => (
                    <li key={index}>{name}</li>
                ))}
            </ul>
        );
    };

    const periodStr = period ? formatDateRange(period.startDate, period.endDate) : null;

    const groups = [];

    if (brands && brands.length > 0) {
        const displayNames = brands.map(b => {
            const appBrand = brandEntities?.[b.id];
            return appBrand?.displayName || appBrand?.name || b.name || 'Unknown';
        });
        groups.push(
            <div key="brands-stack" className="query-stack-group">
                <div className="query-stack">
                    {brands.slice(0, 4).map((b, i) => (
                        <BrandAvatar key={`brand-icon-${b.id || i}`} brand={b} brandEntities={brandEntities} />
                    ))}
                    {brands.length > 4 && (
                        <Tooltip
                            content={buildNameListTooltip(
                                displayNames.slice(4),
                                'query-brand-tooltip-list'
                            )}
                            placement="top"
                            variant="dark"
                            arrow={true}
                        >
                            <div className="query-stack-icon more-icon" aria-label={`+${brands.length - 4} more brands`}>
                                <span>+{brands.length - 4}</span>
                            </div>
                        </Tooltip>
                    )}
                </div>
            </div>
        );
    }

    if (markets && markets.length > 0) {
        groups.push(
            <div key="markets-stack" className="query-stack-group">
                <div className="query-stack">
                    {markets.slice(0, 4).map((m, i) => {
                        const marketName = m.name || 'Unknown';
                        return (
                            <Tooltip
                                key={`market-icon-${m.id || i}`}
                                content={marketName}
                                placement="top"
                                variant="dark"
                                arrow={true}
                            >
                                <div className="query-stack-icon market-icon" aria-label={marketName}>
                                    <MarketAvatar market={m} />
                                </div>
                            </Tooltip>
                        );
                    })}
                    {markets.length > 4 && (
                        <Tooltip
                            content={buildNameListTooltip(
                                markets.slice(4).map(m => m.name || 'Unknown'),
                                'query-market-tooltip-list'
                            )}
                            placement="top"
                            variant="dark"
                            arrow={true}
                        >
                            <div className="query-stack-icon more-icon" aria-label={`+${markets.length - 4} more markets`}>
                                <span>+{markets.length - 4}</span>
                            </div>
                        </Tooltip>
                    )}
                </div>
                <div className="query-stack-text market-text">
                    {markets.length === 1 ? (markets[0].name || 'Unknown') : markets.map(m => m.name || 'Unknown').join(', ')}
                </div>
            </div>
        );
    }

    if (segments && segments.length > 0) {
        const segmentLines = buildSegmentLines(segments);
        const segmentTooltip = buildSegmentTooltip(segments);
        const segmentLabel = segments.length > 1 ? `${segments.length} Segments` : resolveSegmentName(segments[0]);
        groups.push(
            <div key="segments" className="query-stack-group segment-group">
                <div className="query-stack">
                    <Tooltip content={segmentTooltip} placement="top" variant="dark" arrow={true}>
                        <div className="query-stack-icon segment-icon" aria-label={(segmentLines && segmentLines.join(', ')) || 'Segment'}>
                            <span aria-hidden>S</span>
                        </div>
                    </Tooltip>
                </div>
                <div className="query-stack-text segment-text">
                    {segmentLabel}
                </div>
            </div>
        );
    }

    if (filters && filters.length > 0) {
        const filterLines = buildFilterLines(filters);
        const filterTooltip = buildFilterTooltip(filters);
        const filterLabel = filters.length === 1
            ? buildSingleFilterLabel(filters[0])
            : `${filters.length} Filters`;
        groups.push(
            <div key="filters" className="query-stack-group filter-group">
                <div className="query-stack">
                    <Tooltip content={filterTooltip} placement="top" variant="dark" arrow={true}>
                        <div className="query-stack-icon filter-icon" aria-label={(filterLines && filterLines.join(', ')) || 'Filters'}>
                            <span aria-hidden>F</span>
                        </div>
                    </Tooltip>
                </div>
                <div className="query-stack-text filter-text">
                    {filterLabel}
                </div>
            </div>
        );
    }

    if (periodStr) {
        groups.push(<span key="period" className="query-chip query-period">{periodStr}</span>);
    }

    if (groups.length === 0) return null;

    return (
        <div className="query-context-bar" role="group" aria-label="Query parameters">
            <div className="query-context-chips">
                {groups}
            </div>
        </div>
    );
};

export default QueryContextBar;
